/**
 * ⭐ **The end route — the tablet's half** [03-us-kad-6j-i; decisions 0509, 0518, 0520, 0521].
 *
 * ```
 * POST /enrolment/visit-end   204 — the confirmation | 400 | 401 | 503
 * ```
 *
 * At *Tap to start*, a tablet that holds an end with no confirmation sends it HERE, beside the venue probe
 * (`GET /enrolment/me`), because the socket confirms nothing [0518] and a re-send into a half-open socket would
 * wait the full 5 s with the server up [AC-2]. The live channel decides WHEN it goes and what its answer settles
 * (`liveSocket.ts`); this module is the one call.
 *
 * ⛔ **The body is `{ visitNumber?, unconfirmedStarts? }` and nothing else** [AC-12, AC-14, AC-19]. No kiosk —
 * the kiosk is the `HttpOnly` credential [KAD-6j-e AC-2] — and no candidate. `visitNumber` goes only when the
 * tablet holds one; without it the api ends the open visit with no compare. `unconfirmedStarts` goes only when
 * it is 1 or more: absent is 0 on the api, and a 0 would say nothing.
 *
 * ⛔ **`credentialedFetch`, NOT `kioskFetch`**, and for the probe's reason [`probeEnrolment`; KAD-6i AC-9;
 * `0285`]. The probe beside this call owns the verdict on a dead credential: its 401 lands the PLAIN pad. A
 * 401 here routed through `kioskFetch` would race it with `refuseCredential()` — *"Incorrect PIN"* in front of a
 * staffer who typed nothing [0263]. So a 401 here is just *not confirmed*.
 */
import { credentialedFetch } from './kioskFetch';

const VISIT_END_PATH = '/enrolment/visit-end';

function apiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? '';
}

/** What one end call reports: the visit it closes, when the tablet holds one, and the fallback count. */
export interface VisitEndReport {
  /** The kiosk's visit number as the tablet last read it [0520]. `undefined`: the tablet holds none. */
  readonly visitNumber: number | undefined;
  /** Visits started without a confirmation since the last one [0521]. Memory only. */
  readonly unconfirmedStarts: number;
}

/**
 * Why an end call was not confirmed — the last one is named on the fallback's line [0521; review round 1]: the
 * api's status (`503`: the step did not complete; `401`: the credential; `400`: a defect here), `'network'` (no
 * answer could be read) or `'aborted'` (the 5 s ran out first). ⛔ Never anything about the visit or a candidate.
 */
export type VisitEndCause = number | 'network' | 'aborted';

/** What one end call came to: the confirmation, or not, and then why not. */
export type VisitEndAnswer =
  { readonly confirmed: true } | { readonly confirmed: false; readonly cause: VisitEndCause };

/**
 * Sends one end, and says whether the api CONFIRMED it.
 *
 * ⛔ **204 AND NOTHING ELSE is the confirmation** — the trap `issueEnrolmentToken` guards with the same
 * test. The api answers this route `204` once the visit the call names is over [AC-7, AC-8], and
 * `staticwebapp.config.json` answers an unknown path with a `200` page of HTML, so `response.ok` would
 * confirm an end against an api that never saw it. A 503 (the step did not complete), a 400, a 401, a network
 * error or the caller's abort is *not confirmed*: the caller keeps waiting out its 5 s [AC-6].
 *
 * ⛔ **`Content-Type: application/json` is load-bearing**: without it the api reads an EMPTY body, and an end
 * with no `visitNumber` ends whatever visit is open, with no compare [AC-19].
 *
 * The caller's `signal` is the 5 s from the tap: when it fires, the call is abandoned [AC-6]. Never throws.
 */
export async function sendVisitEnd(
  report: VisitEndReport,
  signal: AbortSignal,
): Promise<VisitEndAnswer> {
  try {
    const response = await credentialedFetch(`${apiBaseUrl()}${VISIT_END_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(bodyOf(report)),
      signal,
    });
    return response.status === 204
      ? { confirmed: true }
      : { confirmed: false, cause: response.status };
  } catch {
    // An abort or a network failure: not confirmed. The fallback's own line is the trace [0521; AC-14].
    return { confirmed: false, cause: signal.aborted ? 'aborted' : 'network' };
  }
}

/** Built field by field, so nothing beside the two can reach the wire [AC-12]. */
function bodyOf({ visitNumber, unconfirmedStarts }: VisitEndReport): Record<string, number> {
  return {
    ...(visitNumber === undefined ? {} : { visitNumber }),
    ...(unconfirmedStarts > 0 ? { unconfirmedStarts } : {}),
  };
}

/**
 * ⭐ **How long a tap waits for the last visit's end to be confirmed: 5 s FROM THE TAP** [03-us-kad-6j-i AC-6;
 * 0509 Ruling 3]. ⛔ Never longer, and never shorter without a confirmation. A `setTimeout` from the tap, not
 * `AbortSignal.timeout`: the same timer aborts the call and ends the wait, so the two cannot disagree.
 */
const VISIT_END_WAIT_MS = 5_000;

/**
 * What a tap's wait for the last end came to [03-us-kad-6j-i AC-6]:
 *
 * * `confirmed` — the end route answered `204`, or no end was waiting for one;
 * * `timedOut` — 5 s after the tap with no confirmation, and the `cause` of the last call's failure
 *   (`'aborted'` when it never answered). ⛔ Not yet a fallback: only a visit that then STARTS is one, and
 *   only then is it logged and counted [AC-6, AC-14];
 * * `abandoned` — the channel was disposed during the wait: the tablet left the enrolled states (a 401 took
 *   it to the pad). ⛔ No visit act follows.
 */
export type VisitEndWait =
  | { readonly outcome: 'confirmed' }
  | { readonly outcome: 'timedOut'; readonly cause: VisitEndCause }
  | { readonly outcome: 'abandoned' };

/**
 * ⭐ **The tap's wait: one end call, and the first of its `204` or 5 s from the tap** [03-us-kad-6j-i AC-6].
 *
 * `onConfirmed` runs on the `204` only, before `wait` resolves `confirmed`. ⛔ A refusal or a failure before 5 s
 * does not end the wait early; it is kept as the `cause` the `timedOut` carries. ⛔ An answer past the 5 s, or
 * after `abandon`, settles nothing. `abandon` aborts the call and resolves `abandoned` at once. The timer is a
 * `setTimeout` from the tap, so the same timer aborts the call and ends the wait.
 */
export function waitForTheConfirmation(
  report: VisitEndReport,
  onConfirmed: () => void,
): { readonly wait: Promise<VisitEndWait>; readonly abandon: () => void } {
  const call = new AbortController();
  let over = false;
  let cause: VisitEndCause = 'aborted';
  let finish: (result: VisitEndWait) => void = () => undefined;
  const wait = new Promise<VisitEndWait>((resolve) => {
    finish = (result) => {
      over = true;
      clearTimeout(fallback);
      resolve(result);
    };
  });
  // ⭐ 5 s from the tap, and the call is abandoned with it [AC-6]: no other end call goes out for this tap.
  const fallback = setTimeout(() => {
    call.abort();
    finish({ outcome: 'timedOut', cause });
  }, VISIT_END_WAIT_MS);
  void sendVisitEnd(report, call.signal).then((answer) => {
    if (over) return;
    if (!answer.confirmed) {
      cause = answer.cause;
      return;
    }
    onConfirmed();
    finish({ outcome: 'confirmed' });
  });
  return {
    wait,
    abandon: () => {
      if (over) return;
      call.abort();
      finish({ outcome: 'abandoned' });
    },
  };
}
